import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useAgents } from "@/features/agents/hooks/use-agents";
import { usePatchConnector } from "@/features/connectors/hooks/use-connectors";
import type { Connector, ConnectorStatus } from "@/features/connectors/types";
import { useTranslation } from "@/features/utils/i18n";
import { useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { AgentField, StatusField } from "../connector-form/fields-channel";

interface Props {
  connector: Connector;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * What can be changed on a connected Meta account: which agent answers, and
 * whether it answers. The account itself is not editable — to use another one,
 * disconnect this one and connect again.
 */
export const MetaEditDialog = ({ connector, open, onOpenChange }: Props) => {
  const { t } = useTranslation("connectors");
  const { company: clientId } = useParams<{ company: string }>();
  const { data: agents = [] } = useAgents();
  const patch = usePatchConnector();
  const [status, setStatus] = useState<ConnectorStatus>(connector.status);
  const [agentId, setAgentId] = useState(connector.agent_id ?? "");
  const meta = connector.configuration?.meta;

  const save = async () => {
    if (!clientId) return;
    try {
      await patch.mutateAsync({
        clientId,
        connectorId: connector.id!,
        data: { status, agent_id: agentId },
      });
      toast.success(t("messages.updated"));
      onOpenChange(false);
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : t("messages.updateError"),
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle>{t("meta.edit.title")}</DialogTitle>
          <DialogDescription>
            {meta ? t(`meta.${meta.channel}.label`) : ""}
            {meta?.display_name ? ` · ${meta.display_name}` : ""}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {meta?.needs_reconnect && (
            <p className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm">
              {t("meta.edit.needsReconnect")}
            </p>
          )}
          {meta?.coexistence && (
            <Badge variant="secondary">{t("meta.edit.coexistence")}</Badge>
          )}
          <StatusField status={status} onChange={setStatus} />
          <AgentField agentId={agentId} onChange={setAgentId} agents={agents} />
          <div className="flex justify-end">
            <Button onClick={save} disabled={patch.isPending}>
              {t("connectors.form.updateButton")}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
